import { TFunction } from "i18next";

import { OptimizationDto } from "#modules/optimizations/infra/computeOptimizations.api";
import {
  getDiscriminantMaterialDimensions,
  getMaterialDimensionsFieldsConfiguration,
  GROUPING_RULES_BY_PROCESS,
  MaterialGradeType,
  type MaterialDimensionField,
  type PartTypeType,
} from "shared";

type Dimensions = {
  height: number | null;
  width: number | null;
  length: number | null;
  tubeThickness: number | null;
};

const materialLabel = (
  materialName: string | null | undefined,
  materialGrade: MaterialGradeType | null | undefined,
  t: TFunction,
) => materialName || (materialGrade ? t(`qrl.materialGrades.${materialGrade}`) : "");

const partTypeOf = (optimization: OptimizationDto) =>
  optimization.quoteRequestLines.find((line) => line.part.type)?.part.type;

const joinParts = (parts: Array<string | null | undefined>) => parts.filter(Boolean).join(" · ");

const dimensionValue = (dimensions: Dimensions, field: MaterialDimensionField) => dimensions[field];

const thicknessLabel = (
  dimensions: Dimensions,
  partType: PartTypeType | null | undefined,
  t: TFunction,
) => {
  const discriminant = new Set(getDiscriminantMaterialDimensions(partType));
  const configs = getMaterialDimensionsFieldsConfiguration(partType);
  const syncedFields = new Set(
    configs.flatMap((config) => (config.syncWith ? [config.syncWith] : [])),
  );

  const tokens = configs.flatMap((config) => {
    if (!discriminant.has(config.field) || syncedFields.has(config.field)) {
      return [];
    }

    const value = dimensionValue(dimensions, config.field);
    if (value === null) {
      return [];
    }

    if (config.key === "thickness") {
      return [t("optimizations.card.thickness", { height: value })];
    }

    if (config.key === "diameter") {
      return [t("optimizations.card.diameter", { diameter: value })];
    }

    return [`${value}`];
  });

  if (tokens.length === 0 && dimensions.height !== null) {
    return t("optimizations.card.thickness", { height: dimensions.height });
  }

  return tokens.join(" · ");
};

const diameterLabel = (dimensions: Dimensions, t: TFunction) => {
  const diameter = dimensions.width ?? dimensions.height;
  return diameter === null ? "" : t("optimizations.card.diameter", { diameter });
};

const dimensionsOf = (metadata: {
  height: number | null;
  width: number | null;
  length: number | null;
  tubeThickness: number | null;
}): Dimensions => ({
  height: metadata.height,
  width: metadata.width,
  length: metadata.length,
  tubeThickness: metadata.tubeThickness,
});

export const getOptimizationTitle = (optimization: OptimizationDto, t: TFunction) => {
  const machineTimes = optimization.machineTimesOptimizationMetadata;
  if (machineTimes) {
    return t("optimizations.card.machineTimesTitle", {
      count: optimization.quoteRequestLines.length,
      process: t(`industrialProcesses.${machineTimes.companyMachine.machine.industrialProcess}`),
    });
  }

  return t("optimizations.card.title", { count: optimization.quoteRequestLines.length });
};

export const getOptimizationSubtitle = (optimization: OptimizationDto, t: TFunction) => {
  const machineTimes = optimization.machineTimesOptimizationMetadata;
  if (machineTimes) {
    const grouping =
      GROUPING_RULES_BY_PROCESS[machineTimes.companyMachine.machine.industrialProcess];
    if (!grouping || grouping === "allLines") {
      return "";
    }

    const partType = partTypeOf(optimization);
    const material = materialLabel(machineTimes.materialName, machineTimes.materialGrade, t);
    const dimensions = dimensionsOf(machineTimes);
    const dimension =
      grouping === "material"
        ? ""
        : grouping === "diameter"
          ? diameterLabel(dimensions, t)
          : thicknessLabel(dimensions, partType, t);

    return joinParts([partType ? t(`part.type.${partType}`) : "", material, dimension]);
  }

  const sheetNesting = optimization.sheetNestingOptimizationMetadata;
  if (!sheetNesting) {
    return "";
  }

  const partType = partTypeOf(optimization);
  const typeLabel = partType ? t(`part.type.${partType}`) : t("optimizations.card.sheet");
  const material = materialLabel(sheetNesting.materialName, sheetNesting.materialGrade, t);
  const thickness = t("optimizations.card.thickness", { height: sheetNesting.height });

  return joinParts([typeLabel, material, thickness]);
};
